import type { LocationQuery, LocationQueryValue } from 'vue-router'
import type {
  DashboardSavedViewFilters,
  DashboardSavedViewState,
  LensCardInfo,
  LensColorBy,
  LensDensity,
  LensGroupBy,
  LensMonthLayout,
  LensPeriod
} from '@/Network/Types/Requests/DashboardSavedViewRequests'

export type LensState = {
  period: LensPeriod
  monthLayout: LensMonthLayout
  density: LensDensity
  groupBy: LensGroupBy
  colorBy: LensColorBy
  cardInfo: LensCardInfo
}

export const DEFAULT_LENS_STATE: Readonly<LensState> = Object.freeze({
  period: 'semana',
  monthLayout: 'semana',
  density: 'confortavel',
  groupBy: 'escala',
  colorBy: 'tipo',
  cardInfo: 'setor'
})

type LensField = keyof LensState

const LENS_FIELDS: Record<LensField, { key: string; values: readonly string[] }> = {
  period: {
    key: 'periodo',
    values: ['semana', 'mes', 'calendario', 'matriz', 'mapa', 'timeline']
  },
  monthLayout: { key: 'layout', values: ['semana', 'continuo'] },
  density: { key: 'densidade', values: ['confortavel', 'compacta', 'minima'] },
  groupBy: { key: 'agrupar', values: ['escala', 'setor', 'especialidade', 'turno', 'nenhum'] },
  colorBy: { key: 'cor', values: ['tipo', 'setor', 'especialidade', 'check', 'equipe'] },
  cardInfo: { key: 'card', values: ['setor', 'especialidade', 'nenhum'] }
}

const FIELDS = Object.keys(LENS_FIELDS) as LensField[]

export const LENS_QUERY_KEYS: readonly string[] = FIELDS.map((field) => LENS_FIELDS[field].key)

const firstValue = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | null =>
  Array.isArray(value) ? (value[0] ?? null) : (value ?? null)

/** `equipe` só vale com QUICK_LENSES: desligada, cai no padrão (como um cliente antigo). */
const isGatedValue = (field: LensField, raw: string): boolean =>
  field === 'colorBy' &&
  raw === 'equipe' &&
  process.env.VUE_APP_ENABLED_DASHBOARD_QUICK_LENSES !== 'true'

function readField(field: LensField, raw: unknown): string {
  return typeof raw === 'string' &&
    LENS_FIELDS[field].values.includes(raw) &&
    !isGatedValue(field, raw)
    ? raw
    : DEFAULT_LENS_STATE[field]
}

export function parseLensQuery(query: LocationQuery): LensState {
  const state = { ...DEFAULT_LENS_STATE } as Record<LensField, string>
  FIELDS.forEach((field) => {
    state[field] = readField(field, firstValue(query[LENS_FIELDS[field].key]))
  })
  return state as LensState
}

export function serializeLensQuery(state: LensState): Record<string, string> {
  const out: Record<string, string> = {}
  FIELDS.forEach((field) => {
    if (state[field] !== DEFAULT_LENS_STATE[field]) out[LENS_FIELDS[field].key] = state[field]
  })
  return out
}

export function stripLensKeys<T extends Record<string, unknown>>(query: T): T {
  const copy: Record<string, unknown> = { ...query }
  LENS_QUERY_KEYS.forEach((key) => delete copy[key])
  return copy as T
}

export const DENSITY_ZOOM_ORDER: readonly LensDensity[] = ['minima', 'compacta', 'confortavel']

export function zoomDensity(current: LensDensity, step: 1 | -1): LensDensity {
  const index = DENSITY_ZOOM_ORDER.indexOf(current)
  const next = Math.min(DENSITY_ZOOM_ORDER.length - 1, Math.max(0, index + step))
  return DENSITY_ZOOM_ORDER[next]
}

export function lensToViewState(
  lens: LensState,
  filters: DashboardSavedViewFilters
): DashboardSavedViewState {
  return { version: 1, ...lens, filters }
}

export function viewStateToLens(state: DashboardSavedViewState): LensState {
  const lens = {} as Record<LensField, string>
  FIELDS.forEach((field) => {
    lens[field] = readField(field, (state as unknown as Record<string, unknown>)[field])
  })
  return lens as LensState
}

function normalizeFilters(filters: DashboardSavedViewFilters): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {}
  Object.keys(filters)
    .sort()
    .forEach((key) => {
      const value = filters[key as keyof DashboardSavedViewFilters]
      if (Array.isArray(value)) {
        if (value.length) out[key] = [...value].sort()
      } else if (value !== undefined && value !== null && value !== '') {
        out[key] = value
      }
    })
  return out
}

export function viewStatesEqual(a: DashboardSavedViewState, b: DashboardSavedViewState): boolean {
  const pick = (s: DashboardSavedViewState) => ({
    ...viewStateToLens(s),
    filters: normalizeFilters(s.filters)
  })
  return JSON.stringify(pick(a)) === JSON.stringify(pick(b))
}
